// DiffViewer — the Run detail page's "Files & diff" tab (FD §22.3), and
// reusable for a `branch_push` / `pull_request_create_or_ready` approval
// preview.
//
// TODO: fetch the run's changed files (a `GET /runs/:id/files` route is not
// scaffolded yet) and render a real unified/side-by-side diff, e.g. via a
// library such as react-diff-viewer or diff2html. This currently only
// renders the shell/placeholder.

import { useQuery } from '@tanstack/react-query'

export interface DiffViewerProps {
  runId: string
  /** Restrict to a single file when embedded in a smaller context (e.g. an approval preview). */
  path?: string
}

export interface ChangedFile {
  path: string
  changeType: 'added' | 'modified' | 'deleted'
  diff: string
}

export function DiffViewer({ runId, path }: DiffViewerProps) {
  const filesQuery = useQuery<ChangedFile[]>({
    queryKey: ['run', runId, 'files', path],
    queryFn: async () => {
      // TODO: GET /runs/:id/files?path=... once that route exists.
      throw new Error('not implemented')
    },
    // Disabled until the endpoint above exists, so this doesn't spam the
    // network with requests guaranteed to fail.
    enabled: false,
  })

  if (!filesQuery.data || filesQuery.data.length === 0) {
    return (
      <div className="panel px-5 py-6 text-base text-ink-3">
        The full diff appears here once the run has pushed its changes.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {filesQuery.data.map((file) => (
        <div key={file.path} className="panel overflow-hidden">
          <header className="flex items-center justify-between border-b border-line bg-sunken px-4 py-2 text-sm">
            <span className="font-mono text-ink">{file.path}</span>
            <span className="capitalize text-ink-3">{file.changeType}</span>
          </header>
          <pre className="overflow-x-auto p-3 font-mono text-xs leading-relaxed">
            {file.diff.split('\n').map((line, index) => (
              <div
                key={index}
                className={
                  line.startsWith('+')
                    ? 'bg-ok-soft text-ok'
                    : line.startsWith('-')
                      ? 'bg-danger-soft text-danger'
                      : 'text-ink-2'
                }
              >
                {line}
              </div>
            ))}
          </pre>
        </div>
      ))}
    </div>
  )
}
